// src/components/ui/ErrorPopup.js
/**
 * Generic modal popup used for errors, warnings, or confirmation prompts.
 *
 * Props:
 *   • `popup`  – { message: string, onConfirm?: Function } | null
 *   • `onClose` – () => void   (called when user dismisses the popup)
 *
 * Design:
 *   • Renders nothing when `popup` is null.
 *   • If `popup.onConfirm` is provided, shows “Proceed / Cancel” buttons;
 *     otherwise shows a single “OK” button.
 *   • Blocks background interaction with a semi-transparent overlay.
 *
 * Tailwind CSS classes assume the project’s existing style tokens
 * (primary-button, outlined-button, custom-container, etc.).
 */

import React, { useEffect } from "react";

const ErrorPopup = ({ popup, onClose }) => {
  /* ------------------------------------------------------------------ */
  /* Close on ESC key — hook must run every render (rule-of-hooks)      */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (!popup) return; // Do nothing when popup is null
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [popup, onClose]);

  // Early-exit render after hooks have run.
  if (!popup) return null;

  const { message, onConfirm } = popup;

  /* ------------------------------------------------------------------ */
  /* Helper triggers                                                    */
  /* ------------------------------------------------------------------ */
  const handleConfirm = async () => {
    // Close this popup first to avoid race conditions with new popups
    onClose();

    if (typeof onConfirm === "function") {
      try {
        await onConfirm();
      } catch (err) {
        // Swallow errors; parent logic will handle via new popups
        console.error("ErrorPopup onConfirm error:", err);
      }
    }
  };

  /* ------------------------------------------------------------------ */
  /* Render                                                             */
  /* ------------------------------------------------------------------ */
  return (
    <div
      className="fixed inset-0 flex items-start justify-center bg-black/40 z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Error popup"
      onClick={onClose} // close on backdrop click
    >
      <div
        className="custom-container p-4 rounded shadow flex flex-col gap-4 max-w-sm w-full"
        onClick={(e) => e.stopPropagation()} // stop inside clicks bubbling
      >
        {/* Message */}
        <p className="whitespace-pre-line">{message}</p>

        {/* Action buttons */}
        {onConfirm ? (
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="primary-button"
              onClick={handleConfirm}
            >
              Proceed
            </button>
            <button type="button" className="outlined-button" onClick={onClose}>
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex justify-end">
            <button type="button" className="primary-button" onClick={onClose}>
              OK
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(ErrorPopup);
